import { Alliance, UserAlliance } from '../types/alliance';
import { QuoteAlliance, UserAllianceWithQuote } from '../types/quote';

const mapAllianceToUserAlliance = (alliance: Alliance, currentProfileId: string): UserAlliance => {
  const isUser1Current = alliance.user1?.id === currentProfileId;
  const otherUser = isUser1Current ? alliance.user2 : alliance.user1;

  return {
    alliance_id: alliance.id,
    user: {
      id: otherUser!.id,
      first_name: otherUser!.first_name,
      last_name: otherUser!.last_name,
      avatar_url: otherUser!.avatar_url,
      bio: otherUser!.bio,
      service_categories: otherUser?.services || [],
      service_category_slugs: otherUser?.services?.map(service => service.slug) || [],
      recent_alliances: otherUser?.recent_alliances || [],
      alliance_count: otherUser?.alliance_count || 0,
      full_access: alliance.status === 'accepted',
    },
    status: alliance.status,
    requested_by: alliance.requested_by,
    request_message: alliance.request_message,
    rejected_reason: alliance.rejected_reason,
    user_highlighted_service_category: isUser1Current
      ? alliance.user_2_highlighted_service_category
      : alliance.user_1_highlighted_service_category,
    is_user1_current: isUser1Current,
  };
};

export const getAllianceUserIdsToIgnore = (alliances: Alliance[]): string[] => {
  const userIds = new Set<string>();

  alliances
    .filter(alliance => alliance.status === 'pending' || alliance.status === 'accepted')
    .forEach(alliance => {
      if (alliance.user1?.id) userIds.add(alliance.user1.id);
      if (alliance.user2?.id) userIds.add(alliance.user2.id);
    });

  return Array.from(userIds);
};

export const getIncomingPendingRequests = (
  alliances: Alliance[],
  currentProfileId: string
): UserAlliance[] => {
  return alliances
    .filter(
      alliance =>
        alliance.status === 'pending' &&
        alliance.requested_by !== currentProfileId &&
        (alliance.user1?.id === currentProfileId || alliance.user2?.id === currentProfileId)
    )
    .map(alliance => mapAllianceToUserAlliance(alliance, currentProfileId));
};

export const getOutgoingPendingRequests = (
  alliances: Alliance[],
  currentProfileId: string
): UserAlliance[] => {
  return alliances
    .filter(alliance => alliance.status === 'pending' && alliance.requested_by === currentProfileId)
    .map(alliance => mapAllianceToUserAlliance(alliance, currentProfileId));
};

export const getActiveAlliances = (
  alliances: Alliance[],
  currentProfileId: string
): UserAlliance[] => {
  return alliances
    .filter(
      alliance =>
        alliance.status === 'accepted' &&
        (alliance.user1?.id === currentProfileId || alliance.user2?.id === currentProfileId)
    )
    .map(alliance => mapAllianceToUserAlliance(alliance, currentProfileId));
};

/**
 * Creates a unified list of UserAllianceWithQuote entities from UserAlliances and existing QuoteAlliances
 * This maintains the separation between UserAlliance and QuoteAlliance data
 */
export const createUserAlliancesWithQuotes = (
  userAlliances: UserAlliance[],
  quoteAlliances: QuoteAlliance[]
): UserAllianceWithQuote[] => {
  // Create a map of existing quote alliances by invited freelancer ID for quick lookup
  const quoteAllianceMap = new Map<string, QuoteAlliance>();
  quoteAlliances.forEach(qa => {
    quoteAllianceMap.set(qa.invited_freelancer.profile_id, qa);
  });

  // Create unified list: each UserAlliance with its corresponding QuoteAlliance (if it exists)
  const unifiedAlliances: UserAllianceWithQuote[] = [];

  userAlliances.forEach(userAlliance => {
    const quoteAlliance = quoteAllianceMap.get(userAlliance.user.id);

    const userAllianceWithQuote: UserAllianceWithQuote = {
      userAlliance,
      quoteAlliance, // undefined for not-yet-invited alliances
    };

    unifiedAlliances.push(userAllianceWithQuote);
  });

  // Sort to place alliances with quoteAlliance first, and within those, those with childQuote first
  return unifiedAlliances.sort((a, b) => {
    // First priority: alliances with quoteAlliance come first
    if (!!a.quoteAlliance !== !!b.quoteAlliance) {
      return a.quoteAlliance ? -1 : 1;
    }

    // If both have quoteAlliance, sort by childQuote existence
    if (a.quoteAlliance && b.quoteAlliance) {
      if (!!a.quoteAlliance.child_quote !== !!b.quoteAlliance.child_quote) {
        return a.quoteAlliance.child_quote ? -1 : 1;
      }
    }

    // Maintain original order for everything else
    return 0;
  });
};

export const canRequestAlliance = (
  allianceStatus: string | null | undefined,
  currentUserId: string | undefined,
  targetUserId: string | undefined,
  hasPermission: boolean
): boolean => {
  if (!hasPermission || !currentUserId || !targetUserId || currentUserId === targetUserId) {
    return false;
  }
  // Alliance can be requested if there is no status, or if the previous alliance was retired or rejected.
  return !allianceStatus || allianceStatus === 'retired' || allianceStatus === 'rejected';
};
